import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { usePublishToEmma } from '../hooks/usePublishToEmma';
import axios from 'axios';
import {
  ArrowLeft, Bell, Loader, ChevronRight, Lightbulb, Link, Trophy,
  RefreshCw, X,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const ICON_MAP = {
  lightbulb: Lightbulb,
  link: Link,
  trophy: Trophy,
};

const PRIORITY_COLORS = {
  high: 'var(--accent-green)',
  medium: 'var(--accent-orange)',
  low: 'var(--accent-blue)',
};

const SmartAlerts = () => {
  const { token } = useAuth();
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);

  const alertSummary = notifications.length > 0
    ? (Array.isArray(notifications) ? notifications : []).map(n => `[${n.priority}] ${n.title}: ${n.message}`).join('\n')
    : null;
  usePublishToEmma('AI Smart Alerts', alertSummary);

  const headers = { Authorization: `Bearer ${token}` };

  const txt = lang === 'nl' ? {
    title: 'Slimme Meldingen',
    subtitle: 'Proactieve meldingen en actiepunten op basis van je bedrijfsdata',
    noAlerts: 'Geen meldingen op dit moment. Gebruik meer tools om slimme meldingen te ontvangen!',
    refresh: 'Vernieuw meldingen',
    dismiss: 'Verberg',
    openTool: 'Open tool',
  } : {
    title: 'Smart Alerts',
    subtitle: 'Proactive alerts and action items based on your business data',
    noAlerts: 'No alerts right now. Use more tools to receive smart alerts!',
    refresh: 'Refresh alerts',
    dismiss: 'Dismiss',
    openTool: 'Open tool',
  };

  useEffect(() => { loadNotifications(); }, []);

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/smart-notifications/generate`, { headers });
      setNotifications(res.data.notifications || []);
    } catch (err) {
      console.error(err);
    } finally { setLoading(false); }
  };

  const dismiss = async (id) => {
    try {
      await axios.post(`${BACKEND_URL}/api/smart-notifications/dismiss/${id}`, {}, { headers });
      setNotifications(prev => (Array.isArray(prev) ? prev : []).filter(n => n.id !== id));
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="tool-page">
      <div className="tool-header">
        <button onClick={() => navigate('/portal/ai-tools')} className="btn-secondary" data-testid="back-btn">
          <ArrowLeft size={16} /> AI Tools
        </button>
        <h1 className="heading-1">
          <Bell size={22} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 8 }} />
          {txt.title}
        </h1>
      </div>
      <div className="tool-content" style={{ maxWidth: '700px' }}>
        <p className="body-medium text-secondary" style={{ marginBottom: '1.5rem' }}>{txt.subtitle}</p>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <Loader className="spin" size={24} />
          </div>
        ) : notifications.length === 0 ? (
          <div className="auto-input-card" style={{ textAlign: 'center' }} data-testid="no-alerts">
            <Bell size={32} style={{ margin: '0 auto 1rem', opacity: 0.3 }} />
            <p className="body-small text-secondary">{txt.noAlerts}</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }} data-testid="alerts-list">
            {(Array.isArray(notifications) ? notifications : []).map((n) => {
              const NIcon = ICON_MAP[n.icon] || Lightbulb;
              return (
                <div key={n.id} className="auto-input-card" style={{
                  borderLeft: `4px solid ${PRIORITY_COLORS[n.priority] || 'var(--accent-blue)'}`,
                  padding: '1rem 1.25rem',
                }} data-testid={`alert-${n.id}`}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                    <NIcon size={18} style={{ color: PRIORITY_COLORS[n.priority], flexShrink: 0, marginTop: 2 }} />
                    <div style={{ flex: 1 }}>
                      <p className="body-small" style={{ fontWeight: 500 }}>
                        {lang === 'nl' ? n.message_nl : n.message_en}
                      </p>
                      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                        {n.action_path && (
                          <button className="btn-primary" style={{ fontSize: '0.75rem', padding: '0.3rem 0.75rem' }}
                            onClick={() => navigate(n.action_path)} data-testid={`alert-action-${n.id}`}>
                            {txt.openTool} <ChevronRight size={12} />
                          </button>
                        )}
                        <button className="btn-secondary" style={{ fontSize: '0.75rem', padding: '0.3rem 0.75rem' }}
                          onClick={() => dismiss(n.id)} data-testid={`alert-dismiss-${n.id}`}>
                          <X size={12} /> {txt.dismiss}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <button className="btn-secondary" style={{ marginTop: '1.5rem' }}
          onClick={loadNotifications} data-testid="refresh-btn">
          <RefreshCw size={16} /> {txt.refresh}
        </button>
      </div>
    </div>
  );
};

export default SmartAlerts;
